/**
 * Palisade CPU AI — public entry point.
 *
 * createAI(level, seed?) -> { level, chooseMove(state) -> Move }
 *
 *   level : 'easy' | 'casual' | 'normal' | 'tough' | 'hard'
 *   seed  : any number (e.g. Date.now() from the UI). Same seed + same
 *           sequence of positions => same moves (seeded mulberry32).
 *
 * Pure browser-compatible JS: depends only on the public core contract
 * (src/core/index.js) — no DOM, no three.js, no Node APIs.
 *
 * Two algorithm families, parameterized rather than duplicated per level:
 *
 *   policy family (easy, casual) — src/ai/easy.js: no opponent-reply
 *     search, works purely off the core API (so it's automatically
 *     correct with characters/gimmicks, no fallback needed). easy
 *     (choosePolicy) has zero opponent-awareness (greedy own-path walk +
 *     sloppy dice); casual (chooseCasual) scores every move — pawn and
 *     wall alike — with one real ply of core eval and plays the best.
 *
 *   search family (normal, tough, hard) — src/ai/classic.js:
 *     normal — 2-ply shallow search, path-length eval, near-path wall
 *              pruning, approximate opponent replies (the historical sim
 *              AI, unparameterized).
 *     tough/hard — exact full-width 2-ply alpha-beta (jump-aware replies,
 *              every wall anchor at root) differing only in wallWeight
 *              (how much they hoard walls) and refineRoot/refineReplies
 *              (hard adds a 4-ply refinement of the top root moves; tough
 *              stops at 2-ply).
 *
 * Characters & gimmicks: the fast lightweight model is CLASSIC-only, so
 * normal/tough/hard automatically fall back to direct core-API evaluation
 * (applyMove + shortestPathLength, which fold in ninja jumps, ice slides,
 * swamp) whenever the position has gimmicks or an unspent active ability.
 * {type:'ability'} moves are generated and evaluated there like any other
 * move. Once all ability charges are spent (and there are no gimmicks) the
 * position becomes classic-evaluable again and the fast model takes over.
 */

import { mulberry32, hashSeed } from './prng.js';
import { choosePolicy, chooseCasual } from './easy.js';
import {
  chooseNormalClassic,
  chooseHardClassic,
  chooseDeepClassic,
} from './classic.js';
import { chooseGeneric } from './generic.js';
import { chooseDeepGeneric } from './generic-deep.js';

export const AI_LEVELS = ['easy', 'casual', 'normal', 'tough', 'hard'];

// policy family (easy, casual) — see easy.js for opt meanings and why
// easy's wall dice is reactive (gated on being behind) rather than
// unconditional or purely random.
const POLICY_PARAMS = {
  easy: {
    pRandomMove: 0.1,
    pWallBehind: 0.9,
    pWallAhead: 0.05,
    wallRadius: 2,
    greedySlack: 1,
    slackChance: 0.35,
  },
  casual: { pRandomMove: 0.08, wallChance: 0.4, wallRadius: 2 },
};

// tough — the exact full-width 2-ply search (chooseHardClassic) WITHOUT the
// 4-ply refinement and with a lighter wall-hoarding weight. Unchanged.
const TOUGH_PARAMS = { wallWeight: 1.2, refineRoot: 0, refineReplies: 0 };

// hard — the deep iterative-deepening alpha-beta minimax (chooseDeepClassic):
// 6 plies, walls at every ply but blocking-orientation only (halves the wall
// branching so depth 6 fits the browser budget), tempo-aware fixed-root eval.
// nodeBudget bounds worst-case think time (~120 ms on the midgame test
// position). See DEEP_DEFAULTS in classic.js for the full rationale + the
// benchmark sweep behind each value.
const HARD_DEEP_PARAMS = {
  wallWeight: 2.2,
  maxPlies: 6,
  tempo: 0.5,
  nodeBudget: 4000,
  crossWalls: false,
  wallPlies: 6,
};

// gimmick/active-ability fallback for the search family. normal keeps the
// exact 2-ply chooseGeneric (src/ai/generic.js). tough/hard use the deep
// ability-aware search (src/ai/generic-deep.js): iterative-deepening
// alpha-beta on the core API with an ability-holding value term (so an
// unspent ability is a held asset, spent only when the read-out pays) and a
// ninja path-distortion blend. This makes the harder tiers actually use
// wallJump / removeWall when it wins, instead of the 2-ply fallback that
// never saw far enough to justify spending a charge. tough is lighter
// (shallower + smaller budget + no wall-hoarding) than hard, mirroring the
// tough/hard split on the classic path.
const NORMAL_GENERIC_OPTS = { topK: 8, wallWeight: 0.1 };
const TOUGH_DEEP_GENERIC_OPTS = {
  wallWeight: 1.2,
  maxPlies: 3,
  nodeBudget: 350,
  rootBeam: 6,
  wallPlies: 2,
};
const HARD_DEEP_GENERIC_OPTS = {
  wallWeight: 2.2,
  maxPlies: 4,
  nodeBudget: 550,
  rootBeam: 8,
  wallPlies: 2,
};

/**
 * Can this position be evaluated by the lightweight classic model?
 * True when there are no tile gimmicks and no player has an unspent
 * active ability (passive-only characters like heavyweight only change
 * wallsLeft, which the model reads from the state anyway).
 */
export function isClassicEval(state) {
  const gimmicks = state.config.gimmicks;
  if (gimmicks && gimmicks.length > 0) return false;
  return state.players.every((p) => !p.ability || p.ability.usesLeft <= 0);
}

/**
 * Internal factory taking an explicit rng (() => [0,1) float). Used by the
 * sim harness so per-game/per-player streams can be derived externally.
 */
export function createAIWithRng(level, rng) {
  if (!AI_LEVELS.includes(level)) {
    throw new Error(
      `Unknown AI level: ${level} (expected ${AI_LEVELS.join(' | ')})`
    );
  }
  return {
    level,
    chooseMove(state) {
      if (level === 'easy') return choosePolicy(state, rng, POLICY_PARAMS.easy);
      if (level === 'casual') return chooseCasual(state, rng, POLICY_PARAMS.casual);
      if (isClassicEval(state)) {
        if (level === 'normal') return chooseNormalClassic(state, rng);
        if (level === 'tough') return chooseHardClassic(state, rng, TOUGH_PARAMS);
        return chooseDeepClassic(state, rng, HARD_DEEP_PARAMS);
      }
      if (level === 'normal') return chooseGeneric(state, rng, NORMAL_GENERIC_OPTS);
      if (level === 'tough') return chooseDeepGeneric(state, rng, TOUGH_DEEP_GENERIC_OPTS);
      return chooseDeepGeneric(state, rng, HARD_DEEP_GENERIC_OPTS);
    },
  };
}

/**
 * Public factory. `seed` may be any number (fractional part is ignored;
 * Date.now() is fine). Omitting it gives a fixed default seed, i.e. a
 * deterministic AI — pass a varying seed for varied games.
 */
export function createAI(level, seed = 1) {
  return createAIWithRng(level, mulberry32(hashSeed(Math.floor(seed))));
}
